import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiTrash2 } from "react-icons/fi";
import { BsPencil } from "react-icons/bs";
import { CiSettings } from "react-icons/ci";
import { IoIosAdd } from "react-icons/io";

import api from "../../../api/axios";
import SubGroupDefaultSequence from "../SubGroup/SubGroupDefaultSequence";
import SaveStatus from "../../Common/SaveStatus";
import { useDebouncedAutosave } from "../../../hooks/useDebouncedAutosave";
import "./SectionManager.css";

const DEFAULT_KEY = "default";
const CUSTOM = "__custom__";

const lc = (v) => (v || "").trim().toLowerCase();
const nameOf = (s) => (s.name || "").trim();
const keyOf = (names) => names.map(lc).sort().join("|");

// Sections in the order of the default sequence; sections missing from it go last.
const orderSections = (scenario) => {
  const sections = scenario?.sections ?? [];
  const seq = (scenario?.sequences ?? []).find((s) => s.key === DEFAULT_KEY);
  const rank = new Map(
    (seq?.items ?? []).map((i) => [String(i.sectionId), i.order])
  );
  return [...sections].sort(
    (a, b) =>
      (rank.get(String(a.id)) ?? 1e9) - (rank.get(String(b.id)) ?? 1e9)
  );
};

// ------------------------------------------------------
// PICK THE SUBGROUP NAMES A CUSTOM SECTION MATCHES
// ------------------------------------------------------
const MatchPicker = ({ options, value, onChange }) => {
  const [filter, setFilter] = useState("");

  const selected = new Set(value.map(lc));
  const all = [...new Set([...options, ...value])].sort((a, b) =>
    a.localeCompare(b)
  );
  const shown = all.filter((o) => lc(o).includes(lc(filter)));

  const toggle = (name) =>
    onChange(
      selected.has(lc(name))
        ? value.filter((v) => lc(v) !== lc(name))
        : [...value, name]
    );

  return (
    <div className="sx-picker">
      <input
        className="sx-input"
        placeholder="Filter devices productGroup..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <div className="sx-picker-list">
        {shown.map((o) => (
          <label key={o} className="sx-picker-item">
            <input
              type="checkbox"
              checked={selected.has(lc(o))}
              onChange={() => toggle(o)}
            />
            {o}
          </label>
        ))}
        {shown.length === 0 && (
          <div className="sx-empty">No devices productGroup found.</div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------
// ROW
// ------------------------------------------------------
const SectionRow = ({
  section,
  isCustom,
  matches,
  count,
  conflict,
  libraryNames,
  onChange,
  onDelete,
  onOpenSequence,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: section.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`sx-row-wrap ${conflict ? "sx-row-conflict" : ""}`}
    >
      <div className="sx-row">
        <div className="sx-handle" {...attributes} {...listeners} title="Drag to reorder">
          ☰
        </div>

        {isCustom ? (
          <input
            className="sx-input"
            value={section.name}
            placeholder="Section name"
            onChange={(e) => onChange(section.id, { name: e.target.value })}
          />
        ) : (
          <div className="sx-name">{section.name}</div>
        )}

        <div className="sx-matches" title={matches.join(", ")}>
          {matches.length ? matches.join(", ") : "-"}
        </div>

        <div className={`sx-count ${count === 0 ? "sx-count-empty" : ""}`}>
          {count === undefined
            ? "…"
            : count === null
            ? "?"
            : count === 0
            ? "No devices"
            : `${count} device${count === 1 ? "" : "s"}`}
        </div>

        <div className="sx-actions">
          {isCustom ? (
            <button
              className={`sx-icon-btn ${pickerOpen ? "active" : ""}`}
              title="Choose matching device productGroups"
              onClick={() => setPickerOpen((o) => !o)}
            >
              <BsPencil />
            </button>
          ) : (
            <span className="sx-icon-slot" aria-hidden="true" />
          )}
          <button
            className="sx-icon-btn"
            title="Device placement sequence"
            onClick={() => onOpenSequence(section)}
          >
            <CiSettings />
          </button>
          <button
            className="sx-icon-btn danger"
            title="Delete"
            onClick={() => onDelete(section.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      </div>

      {conflict && <div className="sx-conflict">{conflict}</div>}

      {isCustom && pickerOpen && (
        <div className="sx-picker-wrap">
          <div className="sx-hint">
            Select the Device's productGroup that belong to this section. With none
            selected, Devices productGroup like the section are used.
          </div>
          <MatchPicker
            options={libraryNames}
            value={section.subGroupNames || []}
            onChange={(v) => onChange(section.id, { subGroupNames: v })}
          />
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------
// MAIN
// ------------------------------------------------------
const SectionManager = ({ scenario, onScenarioUpdated }) => {
  const [sections, setSections] = useState([]);
  const [error, setError] = useState(""); // "add section" form errors

  const [catalog, setCatalog] = useState([]);
  const [libraryNames, setLibraryNames] = useState([]);

  // "add section" form
  const [choice, setChoice] = useState("");
  const [customName, setCustomName] = useState("");
  const [customMatches, setCustomMatches] = useState([]);

  const [counts, setCounts] = useState({});
  const [sequenceSection, setSequenceSection] = useState(null);

  // true once the user changed something -> autosave is active
  const editedRef = useRef(false);

  // Latest values for the (debounced) save callback
  const scenarioRef = useRef(scenario);
  scenarioRef.current = scenario;
  const onUpdatedRef = useRef(onScenarioUpdated);
  onUpdatedRef.current = onScenarioUpdated;

  const sensors = useSensors(useSensor(PointerSensor));

  // ---------------- derived ----------------
  const catalogByName = Object.fromEntries(
    catalog.map((c) => [lc(c.name), c.subGroupNames])
  );

  // The catalog decides: a section is standard if its name is in the catalog,
  // otherwise custom. Stored flags/names on old documents are ignored for standard sections.
  const isCustomSection = (s) =>
    catalog.length > 0 ? !catalogByName[lc(s.name)] : !!s.isCustom;

  // SubGroup names a section matches: catalog (standard) > own selection (custom) > its own name
  const effectiveNames = (s) => {
    const fromCatalog = catalogByName[lc(s.name)];
    if (fromCatalog) return fromCatalog;
    const own = (s.subGroupNames || []).map((n) => n.trim()).filter(Boolean);
    if (own.length) return own;
    return nameOf(s) ? [nameOf(s)] : [];
  };

  // which sections claim which SubGroup name (a name may belong to one section only)
  const claimsOf = (list) => {
    const claims = new Map();
    list.forEach((s) =>
      effectiveNames(s).forEach((n) =>
        claims.set(lc(n), [...(claims.get(lc(n)) || []), nameOf(s)])
      )
    );
    return claims;
  };

  const conflictIn = (s, claims) => {
    for (const n of effectiveNames(s)) {
      const owners = claims.get(lc(n)) || [];
      if (owners.length > 1) {
        const others = owners.filter((o) => o !== nameOf(s));
        return `Device productGroup "${n}" is also matched by ${others.join(", ")}.`;
      }
    }
    return "";
  };

  // First problem that blocks saving, or "" if the list is valid
  const problemOf = (list) => {
    const claims = claimsOf(list);
    const nameCount = {};
    list.forEach((s) => {
      nameCount[lc(s.name)] = (nameCount[lc(s.name)] || 0) + 1;
    });
    for (const s of list) {
      if (!nameOf(s)) return "Every section needs a name.";
      if (nameCount[lc(s.name)] > 1)
        return `Section "${nameOf(s)}" exists more than once.`;
      const c = conflictIn(s, claims);
      if (c) return c;
    }
    return "";
  };

  const claims = claimsOf(sections);
  const conflictFor = (s) => conflictIn(s, claims);
  const problem = problemOf(sections);

  const usedNames = new Set(sections.map((s) => lc(s.name)));
  const available = catalog.filter((c) => !usedNames.has(lc(c.name)));

  // ---------------- payload ----------------
  const buildPayload = (list, sc) => {
    const ids = new Set(list.map((s) => s.id));
    const ordered = list.map((s, i) => {
      const custom = isCustomSection(s);
      return {
        ...s,
        scenarioId: sc.id,
        name: nameOf(s),
        order: i + 1,
        isCustom: custom,
        subGroupNames: custom
          ? (s.subGroupNames || []).map((n) => n.trim()).filter(Boolean)
          : [],
      };
    });
    const defaultItems = ordered.map((s, i) => ({ sectionId: s.id, order: i + 1 }));

    const sequences = (sc.sequences ?? []).map((seq) =>
      seq.key === DEFAULT_KEY
        ? { ...seq, items: defaultItems }
        : {
            ...seq,
            items: (seq.items ?? [])
              .filter((i) => ids.has(i.sectionId))
              .sort((a, b) => a.order - b.order)
              .map((i, idx) => ({ ...i, order: idx + 1 })),
          }
    );
    if (!sequences.some((s) => s.key === DEFAULT_KEY)) {
      sequences.unshift({
        key: DEFAULT_KEY,
        name: "Default",
        isDefault: true,
        items: defaultItems,
      });
    }

    const subGroupSequenceGroups = (sc.subGroupSequenceGroups ?? []).map((g) => ({
      ...g,
      selections: (g.selections ?? []).filter((x) => ids.has(x.sectionId)),
    }));

    return { sections: ordered, sequences, subGroupSequenceGroups };
  };

  // ---------------- autosave ----------------
  // Only sections + sequences + subgroup groups, via PUT /scenario/{id}/structure,
  // so it never overwrites name/description/settings saved elsewhere.
  const { status, schedule } = useDebouncedAutosave(async (list) => {
    const sc = scenarioRef.current;
    if (!sc?.id) return;
    const payload = buildPayload(list, sc);
    await api.put(`/scenario/${sc.id}/structure`, payload);
    onUpdatedRef.current?.(payload);
  }, 800);

  // Every valid change by the user is saved automatically.
  // Invalid states (empty name, duplicate, conflict) are not saved; the last
  // valid state stays in the database until the problem is fixed.
  useEffect(() => {
    if (!editedRef.current) return;
    if (problemOf(sections)) return;
    schedule(sections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, catalog]);

  // ---------------- load ----------------
  // Re-init only when a different scenario is opened. Our own autosaves also
  // update the parent's scenario; re-initialising then would throw away edits
  // made while the request was running.
  useEffect(() => {
    editedRef.current = false;
    setSections(orderSections(scenario));
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.id]);

  useEffect(() => {
    api
      .get("/section/catalog")
      .then((r) => setCatalog(r.data || []))
      .catch((err) => console.error("Failed to load section catalog:", err));
    api
      .get("/section/subgroup-names")
      .then((r) => setLibraryNames(r.data || []))
      .catch((err) => console.error("Failed to load device productGroups:", err));
  }, []);

  // Count matching subgroups per distinct set of names (debounced, cached)
  useEffect(() => {
    const wanted = new Map();
    sections.forEach((s) => {
      const names = effectiveNames(s);
      if (names.length) wanted.set(keyOf(names), names);
    });
    const missing = [...wanted].filter(([k]) => counts[k] === undefined);
    if (!missing.length) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      const entries = await Promise.all(
        missing.map(([k, names]) =>
          api
            .post("/section/subgroups/match", names)
            .then((r) => [k, r.data.length])
            .catch(() => [k, null])
        )
      );
      if (!cancelled) {
        setCounts((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, catalog]);

  // ---------------- edit ----------------
  const edit = (updater) => {
    editedRef.current = true;
    setSections(updater);
  };

  const change = (id, patch) =>
    edit((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const remove = (id) => {
    if (!window.confirm("Delete this section?")) return;
    edit((prev) => prev.filter((s) => s.id !== id));
  };

  const makeSection = (name, isCustom, subGroupNames) => ({
    id: uuidv4(),
    scenarioId: scenario.id,
    name,
    isCustom,
    subGroupNames,
    isDefault: false,
    subGroupSequences: [
      { key: DEFAULT_KEY, name: "Default", isDefault: true, groupOrders: [] },
    ],
    activeSubGroupSequenceKey: DEFAULT_KEY,
  });

  const add = () => {
    let section;

    if (choice === CUSTOM) {
      const name = customName.trim();
      if (!name) {
        setError("Enter a name for the custom section.");
        return;
      }
      if (usedNames.has(lc(name))) {
        setError(`"${name}" is already in this scenario.`);
        return;
      }
      if (catalogByName[lc(name)]) {
        setError(`"${name}" is a standard section. Pick it from the list.`);
        return;
      }
      section = makeSection(name, true, customMatches);
    } else {
      if (!available.some((c) => c.name === choice)) {
        setError("Choose a section from the list.");
        return;
      }
      section = makeSection(choice, false, []);
    }

    edit((prev) => [...prev, section]);
    setChoice("");
    setCustomName("");
    setCustomMatches([]);
    setError("");
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    edit((prev) => {
      const from = prev.findIndex((s) => s.id === active.id);
      const to = prev.findIndex((s) => s.id === over.id);
      return arrayMove(prev, from, to);
    });
  };

  // ---------------- render ----------------
  return (
    <div className="sx-container">
      <div className="sx-status-row">
        <SaveStatus status={problem && editedRef.current ? "blocked" : status} />
      </div>

      <div className="sx-add">
        <div className="sx-add-row">
          <select
            className="sx-input"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
          >
            <option value="">Choose a section...</option>
            {available.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
            <option value={CUSTOM}>Custom section...</option>
          </select>
          <button className="sx-btn sx-btn-add" onClick={add} disabled={!choice}>
            <span className="sx-btn-icon"><IoIosAdd /></span>Add Section
          </button>
        </div>

        {choice === CUSTOM && (
          <div className="sx-custom">
            <input
              className="sx-input"
              placeholder="Custom section name"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
            />
            <div className="sx-hint">
              Select the Device's productGroup that belong to this section. With none
              selected, Devices productGroup like the section's name are used (eg. Converters -- Converters).
            </div>
            <MatchPicker
              options={libraryNames}
              value={customMatches}
              onChange={setCustomMatches}
            />
          </div>
        )}
      </div>

      <div className="sx-head">
        <span />
        <span>Section</span>
        <span>Matches Devices productGroup</span>
        <span>Found</span>
        <span />
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={sections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map((s) => {
            const matches = effectiveNames(s);
            return (
              <SectionRow
                key={s.id}
                section={s}
                isCustom={isCustomSection(s)}
                matches={matches}
                count={counts[keyOf(matches)]}
                conflict={conflictFor(s)}
                libraryNames={libraryNames}
                onChange={change}
                onDelete={remove}
                onOpenSequence={setSequenceSection}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      {sections.length === 0 && (
        <div className="sx-empty">No sections yet. Add the first one above.</div>
      )}

      {(error || problem) && <div className="sx-error">{error || problem}</div>}

      <SubGroupDefaultSequence
        isOpen={!!sequenceSection}
        onClose={() => setSequenceSection(null)}
        section={sequenceSection}
        matchNames={sequenceSection ? effectiveNames(sequenceSection) : []}
        onSectionUpdated={(updated) => {
          // applied to the list -> saved automatically
          edit((prev) =>
            prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
          );
        }}
      />
    </div>
  );
};

export default SectionManager;
